import {compute, Signal, signal} from "../../fjsc/src/signals.ts";
import {Api} from "../api/api.ts";
import {Generics} from "./generics.ts";
import {create, ifjs, signalMap} from "../../fjsc/src/f2.ts";
import {Track} from "../models/db/tri/Track.ts";
import {Route} from "../routing/Route.ts";
import {navigate} from "../routing/Router.ts";
import {dayFrom} from "../functions/dates.ts";
import {getImageUrl} from "../functions/templates.ts";
import {MediaFileType} from "../enums/MediaFileType.ts";
import {RequestableImageSize} from "./requestableImageSize.ts";
import {ServiceLink} from "../models/ServiceLink.ts";
import {LinkServices} from "../enums/LinkServices.ts";
import {Time} from "../functions/Time.ts";
import {Artists} from "./artists.ts";

const tracks = signal<Track[]>([]);

export class Tracks {
    static trackPage(route: Route, params: any) {
        const track = signal<Track|null>(null);
        const loading = signal(false);
        if (!params.id) {
            const urlParams = new URLSearchParams(window.location.search);
            params.id = urlParams.get("id");
            if (!params.id) {
                return Generics.notFound();
            }
        }
        Api.getTrack(params.id ?? 0)
            .then(a => track.value = a)
            .finally(() => loading.value = false);

        return Generics.pageFrame(
            create("div")
                .classes("flex-v")
                .children(
                    ifjs(loading, Generics.loading()),
                    ifjs(track, Tracks.track(track))
                ).build()
        );
    }

    private static track(track$: Signal<Track | null>) {
        const title = compute(t => t?.title ?? "", track$);
        const artists = compute(t => t?.artists ?? "", track$);
        const releaseDate = compute(t => {
            return dayFrom(t?.release_date ?? new Date());
        }, track$);
        const id = compute(t => t?.id ?? 0, track$);
        const src = compute(i => getImageUrl(MediaFileType.trackCover, i, RequestableImageSize.s500), id);
        const albumTitle = compute(t => t?.album?.title ?? "Unknown album", track$);
        const albumUrl = compute(t => "/album/" + t?.album?.id, track$);

        return create("div")
            .classes("flex-v", "fill-parent", "relative")
            .children(
                Generics.image(src, ["background-cover"]),
                create("div")
                    .classes("flex-v", "centered-content")
                    .children(
                        Generics.image(src, ["cover"]),
                        create("div")
                            .classes("flex-v")
                            .children(
                                Generics.heading(1, title, ["bold", "small-line"]),
                                create("div")
                                    .classes("flex")
                                    .children(
                                        create("span")
                                            .classes("artist-name")
                                            .text("Track by")
                                            .build(),
                                        Artists.artistsFromString(artists),
                                    ).build(),
                                create("div")
                                    .classes("flex", "center-items")
                                    .children(
                                        create("i")
                                            .text("in album")
                                            .build(),
                                        Generics.link(albumUrl, albumTitle)
                                    ).build(),
                                Generics.date(releaseDate),
                                Generics.heading(2, "Links"),
                                Tracks.serviceLinks(track$),
                            ).build()
                    ).build()
            ).build();
    }

    static serviceLinks(track: Signal<Track | null>) {
        const linkMap = compute((t: Track|null) => new Map<LinkServices, string|null|undefined>([
            [LinkServices.lyda, t?.link_lyda],
            [LinkServices.bandcamp, t?.link_bandcamp],
            [LinkServices.applemusic, t?.link_applemusic],
            [LinkServices.youtube, t?.link_youtube],
            [LinkServices.soundcloud, t?.link_soundcloud],
            [LinkServices.spotify, t?.link_spotify],
        ]), track);
        const links = compute(m => Array.from(m.values()).filter(l => l), linkMap);

        return signalMap(links, create("div").classes("flex"), link => {
            const service = linkMap.value.keys().find(s => link?.includes(s));
            return Tracks.serviceLink(<ServiceLink>{
                service,
                link
            })
        });
    }

    static serviceLink(link: ServiceLink) {
        const fileMap = new Map<LinkServices, string>([
            [LinkServices.youtube, "youtube.png"],
            [LinkServices.applemusic, "apple.svg"],
            [LinkServices.bandcamp, "bandcamp.png"],
            [LinkServices.soundcloud, "soundcloud.svg"],
            [LinkServices.spotify, "spotify.svg"],
            [LinkServices.lyda, "lyda.svg"],
        ]);
        const src = "/images/services/" + fileMap.get(link.service as LinkServices);

        return create("div")
            .classes("flex", "center-items", "service-link", "noflexwrap", link.service)
            .onclick(() => {
                window.open(link.link, "_blank");
            })
            .children(
                Generics.image(src, ["service-logo"]),
                create("span")
                    .text(link.service)
                    .build(),
            ).build();
    }

    static tracksList(tracks: Signal<Track[]>) {
        return Generics.table(
            ["", "Track title", "Artists", "Release date", "Length"],
            tracks,
            (track) => create("tr")
                .children(
                    create("td")
                        .children(
                            Generics.image(getImageUrl(MediaFileType.trackCover, track.id, RequestableImageSize.s50))
                        ).build(),
                    create("td")
                        .children(
                            Generics.link("/track/" + track.id, track.title)
                        ).build(),
                    create("td")
                        .children(Artists.artistsFromString(track.artists, false))
                        .build(),
                    create("td")
                        .text(dayFrom(track.release_date))
                        .build(),
                    create("td")
                        .classes("hideOnSmallBreakpoint")
                        .text(Time.toTimeFromSeconds(track.length))
                        .build()
                ).build()
        );
    }

    static tracksTab() {
        const filter = signal("");
        const filteredTracks = compute((a, f) => a.filter(a => {
            return a.title.toLowerCase().includes(f.toLowerCase()) ||
                a.artists.toLowerCase().includes(f.toLowerCase());
        }), tracks, filter);
        const count = compute(a => a.length + " Tracks", filteredTracks);
        const loading = signal(false);
        Api.getTracks()
            .then(a => tracks.value = a)
            .finally(() => loading.value = false);

        return create("div")
            .classes("flex-v")
            .children(
                Generics.heading(2, count),
                ifjs(loading, Generics.loading()),
                Tracks.tracksList(filteredTracks)
            ).build();
    }
}